import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useId, useState } from 'react';
import { useForm } from 'react-hook-form';

import { ApiError } from '@shared/api/client';
import { resolveErrorCopy } from '@shared/errors/catalog';
import { Dialog } from '@shared/ui/dialog';
import { EditIcon } from '@shared/ui/icons';
import { StateBadge } from '@shared/ui/StateBadge';
import { useToast } from '@shared/ui/toast/ToastProvider';

import { DetailCard, DetailSection, MetaGrid, SectionAction } from '../_shared/components';
import { roleLabel, serviceLabel, sharedCopy } from '../_shared/copy';
import { ACCOUNT_ROLES } from '../_shared/roles';
import type { AdminUserDetail } from '../_shared/types';
import { copy } from './copy';
import { errors } from './errors';
import { roleFormSchema, type RoleFormValues } from './forms.schema';
import { useReplaceAccountRole } from './mutations';
import type { AskConfirm } from './types';

/**
 * §2 الدور — one vocabulary, one control, one dialog.
 *
 * **Grant and revoke are one control, because an account holds one role.** The
 * operation is a *change*, not two verbs: «سحب الدور» is «مشترك» selected in the
 * same field. Two buttons would draw a state machine the data model does not
 * have and would leave «امنح» meaningless on an account that already holds a
 * role.
 *
 * **There is no second control.** This section used to draw a «المراجعة
 * التحريرية» checkbox writing the role name `editorial_reviewer`, which no
 * service has ever seeded — the grant it offered could not succeed. `content`'s
 * `operator` now carries `editorial_review`, so the account role is the whole
 * answer, and the two-person rule is where it always was: a post is approved by
 * someone other than its writer.
 *
 * **The badge is neutral on every value.** A granted role is not a state; the
 * badge taxonomy is closed, and a colour for a role would say that holding one
 * is an achievement the platform is congratulating.
 *
 * **The role that is granted is not necessarily in force.** Authorization is
 * live the moment it is written; the *public* face waits for the qualifying
 * action, and the section says so rather than letting the badge imply otherwise.
 *
 * **The write passes a confirmation that names the escalation before it
 * applies.** Nothing durably records a role change while the audit log is
 * deferred, so the confirm step is the only guard that ships — which is also why
 * it names both the value being left and the value being taken.
 */
export function RolesSection({ user, askConfirm }: { user: AdminUserDetail; askConfirm: AskConfirm }) {
  const [open, setOpen] = useState(false);
  const toast = useToast();

  const replaceAccountRole = useReplaceAccountRole(user.id);

  function onWriteFailure(error: unknown) {
    const status = error instanceof ApiError ? error.status : 0;
    const code = error instanceof ApiError ? error.code : undefined;
    toast.show(resolveErrorCopy(code, status, errors).message, 'danger');
  }

  function submit(values: RoleFormValues) {
    // A no-op never reaches the confirmation: re-selecting the value the account
    // already holds closes the dialog and writes nothing. Seven idempotent
    // `PUT`s would be harmless on the wire and dishonest on the screen — the
    // toast would announce a change nobody made.
    if (values.accountRole === user.role) {
      setOpen(false);
      return;
    }

    setOpen(false);

    askConfirm(
      {
        heading: copy.roles.confirm.heading(user.display_name),
        body: copy.roles.confirm.body(roleLabel(user.role), roleLabel(values.accountRole)),
        commitLabel: copy.roles.confirm.commit,
        intent: 'warning',
      },
      async () => {
        // One name, written to every service that seeds it. The mutation reports
        // which peers refused rather than throwing: nothing spans the seven, so
        // a partial write has no rollback to offer and the operator is told
        // which parts to re-send.
        const missed = await replaceAccountRole.mutateAsync(values.accountRole);

        if (missed.length === 0) {
          toast.show(copy.roles.saved, 'success');
          return;
        }
        toast.show(copy.roles.partial(missed.map(serviceLabel).join('، ')), 'danger');
      },
      onWriteFailure,
    );
  }

  return (
    <>
      <DetailSection
        id="user-roles"
        heading={copy.roles.heading}
        context={copy.roles.context}
        action={
          <SectionAction onClick={() => setOpen(true)} icon={<EditIcon />}>
            {copy.roles.edit}
          </SectionAction>
        }
      >
        <DetailCard>
          <div className="p-4">
            <MetaGrid>
              <dt>{copy.roles.accountRole}</dt>
              <dd>
                <StateBadge kind="neutral">{roleLabel(user.role)}</StateBadge>
              </dd>

              <dt>{copy.roles.accountOpens}</dt>
              <dd>{copy.roles.accountOpensValue}</dd>
            </MetaGrid>

            <p className="mb-0 mt-4 max-w-measure text-xs text-ink-muted">{copy.roles.twoPerson}</p>

            {/*
              The conditional grant, stated unconditionally: it describes what
              «غيّر الأدوار» will do rather than what this account's row happens
              to hold, and the operator has to know it **before** they press. An
              earlier revision showed it only while a grant was pending, which
              hid the rule from every operator about to create one.
            */}
            <p className="mb-0 mt-2 max-w-measure text-xs text-ink-muted">{copy.roles.notYetEffective}</p>
          </div>
        </DetailCard>
      </DetailSection>

      <RoleDialog
        open={open}
        user={user}
        busy={replaceAccountRole.isPending}
        onSubmit={submit}
        onCancel={() => setOpen(false)}
      />
    </>
  );
}

/**
 * The dialog is a native `<dialog>` opened with `showModal()` — the focus trap,
 * the backdrop, `Esc`, and focus return to the trigger are the browser's.
 *
 * Its values are seeded from the account every time it opens, so it never holds
 * the previous subject's selection, and the hint under each field explains what
 * the value opens: the explanation belongs beside the control, not in a legend.
 */
function RoleDialog({
  open,
  user,
  busy,
  onSubmit,
  onCancel,
}: {
  open: boolean;
  user: AdminUserDetail;
  busy: boolean;
  onSubmit: (values: RoleFormValues) => void;
  onCancel: () => void;
}) {
  const headingId = useId();
  const accountId = useId();
  const accountHintId = useId();

  const { register, handleSubmit, reset } = useForm<RoleFormValues>({
    resolver: zodResolver(roleFormSchema),
    defaultValues: { accountRole: user.role },
  });

  useEffect(() => {
    if (!open) return;
    reset({ accountRole: user.role });
  }, [open, user.role, reset]);

  return (
    <Dialog
      open={open}
      labelledBy={headingId}
      onClose={(returnValue) => {
        if (returnValue !== 'save') onCancel();
      }}
    >
      <form
        method="dialog"
        onSubmit={(event) => {
          event.preventDefault();
          void handleSubmit(onSubmit)(event);
        }}
        className="flex flex-col gap-4"
      >
        <div>
          <h2 id={headingId} className="text-h3">
            {copy.roles.dialog.title(user.display_name)}
          </h2>
          <p className="mb-0 mt-1 max-w-none text-xs text-ink-muted">{copy.roles.dialog.requiredNote}</p>
        </div>

        <div className="grid gap-2">
          <label htmlFor={accountId} className="text-sm font-medium text-ink">
            {copy.roles.dialog.accountLabel}
            <span aria-hidden="true" className="ms-px text-danger">
              *
            </span>
          </label>
          <select
            id={accountId}
            aria-describedby={accountHintId}
            aria-required
            className="hit-target w-full rounded-sm border border-border-strong bg-surface px-3 text-sm text-ink"
            {...register('accountRole')}
          >
            {ACCOUNT_ROLES.map((role) => (
              <option key={role} value={role}>
                {roleLabel(role)}
              </option>
            ))}
          </select>
          <p id={accountHintId} className="mb-0 max-w-none text-xs text-ink-muted">
            {copy.roles.dialog.accountHint}
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          {/*
            `type="button"`, and the distinction is load-bearing. This form
            intercepts its own `submit` and calls `preventDefault`, so
            `method="dialog"` never runs and a second submit button is not an
            exit — it is a second way to run the save handler. The exit did
            exactly that: pressing «ألغِ» validated the form, passed, opened the
            confirmation, and left an operator who had just declined a change
            looking at a dialog asking them to confirm one. Only `Esc` actually
            left the screen.

            A button that does not submit also has nothing to skip validation
            for, which is why `formNoValidate` is gone with it.
          */}
          <button
            type="button"
            onClick={onCancel}
            className="rounded-sm border border-border-strong px-5 py-3 text-sm text-ink hover:bg-hover-bg"
          >
            {sharedCopy.action.cancel}
          </button>

          <button
            type="submit"
            value="save"
            disabled={busy}
            className="rounded-sm bg-accent px-5 py-3 text-sm text-accent-ink hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-45"
          >
            {copy.roles.dialog.save}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
